import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError, fmtIDR } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, TrendingUp, ShoppingBag, Store, ListOrdered, Landmark, ConciergeBell } from "lucide-react";
import { DISPLAY_FACTOR_EVENT } from "@/components/TopBar";
import jsPDF from "jspdf";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function MonthlySalesDashboard({ onHome }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [bid, setBid] = useState("");
  const [branches, setBranches] = useState([]);
  const [factor, setFactor] = useState(1.0);
  const [data, setData] = useState(null);

  const loadFactor = async () => { try { const { data } = await api.get("/settings/display-factor"); setFactor(data.factor); } catch (e) { } };
  const load = useCallback(async () => {
    try { const { data } = await api.get("/reports/monthly", { params: { month } }); setData(data); }
    catch (e) { toast.error(apiError(e)); }
  }, [month]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/branches"); setBranches(data); } catch (e) { } })(); loadFactor(); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const onEvt = (e) => setFactor(e.detail); window.addEventListener(DISPLAY_FACTOR_EVENT, onEvt); return () => window.removeEventListener(DISPLAY_FACTOR_EVENT, onEvt); }, []);

  const currentMonth = new Date().toISOString().slice(0, 7);
  const isOngoing = month >= currentMonth; // bulan berjalan / masa depan → tampil 100%
  const effFactor = isOngoing ? 1.0 : factor;
  const sc = (n) => Math.round((Number(n) || 0) * effFactor);
  const monthLabel = () => { const [y, m] = month.split("-"); return `${MONTHS_ID[Number(m) - 1]} ${y}`; };
  const rows = data ? (bid ? data.by_branch.filter((b) => b.branch_id === bid) : data.by_branch) : [];
  const sel = bid ? (rows[0] || { food: 0, service: 0, tax: 0, transactions: 0 }) : (data?.total || { food: 0, service: 0, tax: 0, transactions: 0 });
  const branchName = bid ? (branches.find((b) => b.id === bid)?.name || "Cabang") : "Semua Cabang";
  const txns = data ? (bid ? data.transactions.filter((t) => t.branch_id === bid) : data.transactions) : [];

  const downloadPDF = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const W = 210; let y = 16;
    doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text("DA LE TANG HuoGuo", 14, y); y += 6;
    doc.setFontSize(11); doc.setFont("helvetica", "normal");
    doc.text("Ringkasan Penjualan Bulanan", 14, y); y += 6;
    doc.setFontSize(9);
    doc.text(`Bulan   : ${monthLabel()}`, 14, y); y += 5;
    doc.text(`Cabang  : ${branchName}`, 14, y); y += 5;
    doc.text(`Dicetak : ${new Date().toLocaleString("id-ID")}`, 14, y); y += 7;
    doc.setDrawColor(180); doc.line(14, y, W - 14, y); y += 7;

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Ringkasan", 14, y); y += 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text("Omset Bersih", 14, y); doc.text(fmtIDR(sc(sel.food)), W - 14, y, { align: "right" }); y += 6;
    doc.text("PB1", 14, y); doc.text(fmtIDR(sc(sel.tax)), W - 14, y, { align: "right" }); y += 6;
    doc.text("Service Charge", 14, y); doc.text(fmtIDR(sc(sel.service)), W - 14, y, { align: "right" }); y += 6;
    doc.text("Jumlah Transaksi", 14, y); doc.text(String(sel.transactions), W - 14, y, { align: "right" }); y += 9;

    if (!bid) {
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Rincian per Cabang", 14, y); y += 6;
      doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text("Cabang", 14, y); doc.text("Transaksi", 96, y, { align: "right" }); doc.text("Omset Bersih", 138, y, { align: "right" }); doc.text("PB1", 170, y, { align: "right" }); doc.text("Service", W - 14, y, { align: "right" }); y += 4;
      doc.line(14, y, W - 14, y); y += 5;
      doc.setFont("helvetica", "normal"); doc.setFontSize(9);
      rows.forEach((b) => {
        if (y > 285) { doc.addPage(); y = 16; }
        doc.text(String(b.name).slice(0, 28), 14, y);
        doc.text(String(b.transactions), 96, y, { align: "right" });
        doc.text(fmtIDR(sc(b.food)), 138, y, { align: "right" });
        doc.text(fmtIDR(sc(b.tax)), 170, y, { align: "right" });
        doc.text(fmtIDR(sc(b.service)), W - 14, y, { align: "right" });
        y += 6;
      });
      y += 4;
    }

    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text(`Rincian per Transaksi (${txns.length})`, 14, y); y += 6;
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
    doc.text("No Struk", 14, y); doc.text("Waktu", 54, y); doc.text("Kasir", 92, y); doc.text("Omset Bersih", 140, y, { align: "right" }); doc.text("PB1", 170, y, { align: "right" }); doc.text("Service", W - 14, y, { align: "right" }); y += 4;
    doc.line(14, y, W - 14, y); y += 4;
    doc.setFont("helvetica", "normal");
    txns.forEach((t) => {
      if (y > 288) { doc.addPage(); y = 16; }
      doc.text(String(t.receipt_no), 14, y);
      doc.text(new Date(t.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }), 54, y);
      doc.text(String(t.cashier_name || "-").slice(0, 14), 92, y);
      doc.text(fmtIDR(sc(t.net)), 140, y, { align: "right" });
      doc.text(fmtIDR(sc(t.tax)), 170, y, { align: "right" });
      doc.text(fmtIDR(sc(t.service)), W - 14, y, { align: "right" });
      y += 5;
    });
    doc.save(`Penjualan_Bulanan_${branchName.replace(/\s+/g, "_")}_${month}.pdf`);
  };

  return (
    <div className="min-h-screen bg-hotpot-grid pb-10" data-testid="monthly-sales-dashboard">
      <TopBar />
      <div className="mx-auto max-w-[1200px] px-4 pt-6 sm:px-6">
        <button data-testid="sales-home-btn" onClick={onHome} className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-crimson"><ArrowLeft size={16} /> Menu Utama</button>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl">Transaksi Penjualan Bulanan</h1>

        <div className="mb-5 flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Bulan</label>
            <Input data-testid="sales-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-44 bg-neutral-100 border-neutral-200 text-neutral-900" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">Cabang</label>
            <Select value={bid || "all"} onValueChange={(v) => setBid(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="sales-branch" className="h-10 w-56 bg-neutral-100 border-neutral-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Cabang</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto">
            <Button data-testid="sales-download-pdf" onClick={downloadPDF} className="h-10 gap-2 bg-crimson font-bold text-white hover:bg-crimson/90"><Download size={16} /> Cetak PDF</Button>
          </div>
        </div>

        <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="card-surface gold-glow rounded-2xl p-5" data-testid="metric-omset">
            <TrendingUp className="text-gold" size={22} />
            <div className="mt-3 font-mono-num text-2xl font-extrabold text-neutral-900">{fmtIDR(sc(sel.food))}</div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Omset Bersih</div>
          </div>
          <div className="card-surface rounded-2xl p-5" data-testid="metric-pb1">
            <Landmark className="text-crimson" size={22} />
            <div className="mt-3 font-mono-num text-2xl font-extrabold text-neutral-900">{fmtIDR(sc(sel.tax))}</div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">PB1</div>
          </div>
          <div className="card-surface rounded-2xl p-5" data-testid="metric-service">
            <ConciergeBell className="text-crimson" size={22} />
            <div className="mt-3 font-mono-num text-2xl font-extrabold text-neutral-900">{fmtIDR(sc(sel.service))}</div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Service Charge</div>
          </div>
          <div className="card-surface rounded-2xl p-5" data-testid="metric-transaksi">
            <ShoppingBag className="text-crimson" size={22} />
            <div className="mt-3 font-mono-num text-2xl font-extrabold text-neutral-900">{Number(sel.transactions).toLocaleString("id-ID")}</div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Jumlah Transaksi</div>
          </div>
        </div>

        {!bid && (
          <Card className="card-surface mb-5 rounded-2xl p-5" data-testid="branch-breakdown">
            <div className="mb-3 flex items-center gap-2 font-display font-bold text-neutral-900"><Store size={16} className="text-gold" /> Rincian per Cabang — {monthLabel()}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b border-neutral-200 text-[11px] uppercase text-muted-foreground">
                  <th className="py-2 pr-3">Cabang</th><th className="py-2 pr-3 text-right">Transaksi</th><th className="py-2 pr-3 text-right">Omset Bersih</th><th className="py-2 pr-3 text-right">PB1</th><th className="py-2 text-right">Service Charge</th>
                </tr></thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.branch_id} data-testid={`branch-row-${b.branch_id}`} className="border-b border-neutral-200">
                      <td className="py-2 pr-3 text-neutral-900">{b.name}</td>
                      <td className="py-2 pr-3 text-right font-mono-num text-neutral-900">{Number(b.transactions).toLocaleString("id-ID")}</td>
                      <td className="py-2 pr-3 text-right font-mono-num text-gold">{fmtIDR(sc(b.food))}</td>
                      <td className="py-2 pr-3 text-right font-mono-num text-neutral-700">{fmtIDR(sc(b.tax))}</td>
                      <td className="py-2 text-right font-mono-num text-neutral-700">{fmtIDR(sc(b.service))}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">Belum ada transaksi pada bulan ini.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card className="card-surface rounded-2xl p-5" data-testid="transaction-detail">
          <div className="mb-3 flex items-center gap-2 font-display font-bold text-neutral-900"><ListOrdered size={16} className="text-crimson" /> Rincian per Transaksi ({txns.length}) — {branchName}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="transaction-detail-table">
              <thead><tr className="border-b border-neutral-200 text-[11px] uppercase text-muted-foreground">
                <th className="py-2 pr-3">No Struk</th><th className="py-2 pr-3">Waktu</th><th className="py-2 pr-3">Kasir</th><th className="py-2 pr-3 text-right">Item</th><th className="py-2 pr-3">Bayar</th><th className="py-2 pr-3 text-right">Omset Bersih</th><th className="py-2 pr-3 text-right">PB1</th><th className="py-2 text-right">Service</th>
              </tr></thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} data-testid={`tx-row-${t.id}`} className="border-b border-neutral-200">
                    <td className="py-2 pr-3 font-mono-num text-xs text-neutral-900">{t.receipt_no}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-2 pr-3 text-xs text-neutral-900">{t.cashier_name || "-"}</td>
                    <td className="py-2 pr-3 text-right text-xs text-muted-foreground">{t.item_count}</td>
                    <td className="py-2 pr-3 text-xs uppercase text-neutral-900">{t.payment_method}</td>
                    <td className="py-2 pr-3 text-right font-mono-num text-gold">{fmtIDR(sc(t.net))}</td>
                    <td className="py-2 pr-3 text-right font-mono-num text-xs text-neutral-700">{fmtIDR(sc(t.tax))}</td>
                    <td className="py-2 text-right font-mono-num text-xs text-neutral-700">{fmtIDR(sc(t.service))}</td>
                  </tr>
                ))}
                {txns.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">Belum ada transaksi.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
